import { assign, fromPromise, setup } from "xstate";

import {
  MemberConsultationOutcome,
  getMemberConsultations,
  startMemberConsultation,
  getMemberConsultationOutcome,
  GetMemberConsultationsItem,
  getMemberships,
  Member,
  MembershipType,
  submitRating,
  UserRating,
  ServiceRating,
  MemberConsultationType,
  ProductAdministratorSupportedServiceChannelAvailability,
  getMemberProductPaymentRates,
  MemberProductPaymentRatesItem,
  MemberConsultationStatus,
  getDefaultPayGMedicalAssistanceAvailabilityData,
  getMemberServiceOfferingUsage,
  MemberServiceOfferingType,
  MemberServiceOfferingUsage,
  MemberServiceOfferingStatus,
  MemberProfile,
  getMemberProfile,
  getDirectToDoctorPrimarySymptoms,
  Status,
  DirectToDoctorPrimarySymptom,
  getMembershipDependents,
  MembershipDependent,
  updateMembershipDependents,
  MemberProductPaymentRatesChargeCode,
  GetMemberProductPaymentRatesStatus,
  getContractsToSign,
  memberRequestPayment,
  getConsultationPaymentReceipt,
  ConsultationPaymentReceipt,
  applyDiscountVoucher,
  ApplyVoucherResponse,
  findUNU01Membership,
} from "../../../../services/core-api-adapter";
import {
  getCurrentFormattedDay,
  getCurrentFormattedTime,
  isPublicHolidayInSouthAfrica,
  debugLogger,
} from "../../../../utils";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import { ConsultationRoute, ConsultationMatch } from "../../types";

const MAX_POLLING_TIME_IN_SECONDS =
  Number(
    import.meta.env.VITE_APP_INTERCARE_MEDICAL_ASSISTANCE_MAX_POLLING_TIME
  ) || 100;
const POLLING_INTERVAL_IN_MILLISECONDS =
  Number(
    import.meta.env.VITE_APP_INTERCARE_MEDICAL_ASSISTANCE_POLLING_INTERVAL
  ) || 2000;

interface Context {
  activeConsultationListResponse: Array<GetMemberConsultationsItem>;
  memberProfileResponse: MemberProfile | null;
  membersList: Array<Member>;
  currentUserMemberId: string | null;
  memberDependantsList: Array<MembershipDependent>;
  selectedDependent: MembershipDependent | null;
  selectedConsultation: GetMemberConsultationsItem | null;
  consultationOutcomeResponse: GetMemberConsultationsItem | null;
  consultationRatesResponse: MemberProductPaymentRatesItem[] | null;
  consultationType: MemberConsultationType;
  userRating: UserRating;
  consultationAvailabilityData: ProductAdministratorSupportedServiceChannelAvailability[];
  startConsultationTime: Date | null;
  queryParamConsultationId: string;
  queryParamDocMedsBundles: boolean;
  primarySymptomIds: string[];
  isDirectToDoctorSelected: boolean;
  isDoctorMedicineBundleSelected: boolean;
  isNurseConsultationSelected: boolean;
  memberServiceOfferingUsages: MemberServiceOfferingUsage[] | null;
  primarySymptomsResponse: DirectToDoctorPrimarySymptom[] | null;
  isPromoOrVoucherCodeApplied: boolean;
  selectedVoucherOrPromo: MemberServiceOfferingUsage | null;
  contractsToSign: null;
  oneVoucherPin?: string | undefined;
  oneVoucherRedemptionError: any;
  paymentRedirectUrl?: string;
  paymentReceiptResponse?: ConsultationPaymentReceipt | null;
  isFromOrderSummary?: boolean;
  isFromMyPurchases?: boolean;
  selectedDiscountVoucher?: MemberServiceOfferingUsage | null;
  capturedDiscountVoucherCode?: string | null;
  applyDiscountVoucherResponse?: ApplyVoucherResponse | null;
  applyDiscountVoucherResponseError: any;
  isUseVoucherLater: boolean;
  matchedConsultation?: ConsultationMatch | null;
  skipPayment?: boolean;
  detectionError?: Error | null;
}

const defaultAvailabilityData =
  getDefaultPayGMedicalAssistanceAvailabilityData();

/**
 * Maps consultation type selection to charge code for API matching
 * MUST be invoked only when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
 *
 * @param context - State machine context with consultation type flags
 * @returns Charge code string or null if no type selected
 */
export function getChargeCodeForConsultationType(
  context: Context
): string | null {
  if (context.isDirectToDoctorSelected) {
    return "DIGITAL_DOCTOR_CONSULT";
  }
  if (context.isNurseConsultationSelected) {
    return "ONLINE_DOCTOR_CONSULTATION";
  }
  if (context.isDoctorMedicineBundleSelected) {
    return "DIGITAL_DOCTOR_MEDICINE_CONSULT";
  }
  return null;
}

/**
 * Validates if a consultation has a valid HTTPS redirect URL
 * Required when routing to Intercare (PAYMENT_SUCCEEDED outcome)
 * MUST be invoked only when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
 *
 * @param consultation - The consultation to validate
 * @returns True if redirectUrl exists and is an HTTPS URL
 */
export function hasValidRedirectUrl(
  consultation: ConsultationMatch | null
): boolean {
  if (!consultation || !consultation.redirectUrl) {
    return false;
  }

  try {
    const url = new URL(consultation.redirectUrl);
    return url.protocol === "https:";
  } catch {
    return false;
  }
}

const PAYMENT_FLOW_OUTCOMES = [
  "PAYMENT_REQUESTED",
  "PAYMENT_FAILED",
  "TRIAGE_STARTED",
  "TRIAGE_ENDED",
];

const INTERCARE_LINK_OUTCOMES = [
  "SESSION_STARTED",
  "PAYMENT_SUCCEEDED",
  "DIGITAL_CONSULT_STARTED",
  "DIGITAL_CONSULT_ENDED",
  "REFUND_REQUESTED",
  "NO_REFUND",
];

/**
 * Determines routing decision based on consultation outcome
 * MUST be invoked only when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
 *
 * @param existing - Matched consultation or null
 * @returns Route enum indicating which flow to follow
 */
export function getConsultationRoute(
  existing: ConsultationMatch | null
): ConsultationRoute {
  if (!existing || !existing.outcome) {
    return ConsultationRoute.NEW_CONSULTATION;
  }

  if (PAYMENT_FLOW_OUTCOMES.includes(existing.outcome)) {
    return ConsultationRoute.PAYMENT_FLOW;
  }

  if (
    INTERCARE_LINK_OUTCOMES.includes(existing.outcome) &&
    hasValidRedirectUrl(existing)
  ) {
    return ConsultationRoute.INTERCARE_LINK;
  }

  return ConsultationRoute.DEFER_TO_WORKFLOW;
}

/**
 * Maps a ConsultationMatch to GetMemberConsultationsItem format
 * Used when routing based on matched consultation outcome
 *
 * @param match - The matched consultation from idempotency check
 * @returns Formatted consultation item for state machine context
 */
function mapMatchedToSelectedConsultation(
  match: ConsultationMatch
): GetMemberConsultationsItem {
  return {
    consultationId: match.consultationId,
    memberId: match.memberId,
    outcome: match.outcome as MemberConsultationOutcome,
    chargeAmountFormatted: match.chargeAmountFormatted || "",
    chargeCode: match.chargeCode as MemberProductPaymentRatesChargeCode,
    redirectUrl: match.redirectUrl || "",
    serviceProviderName: match.serviceProviderName || "",
    startedOn: match.startedOn,
    status: match.status as MemberConsultationStatus,
    isDependant: match.isDependant ?? false,
    mainMemberId: match.mainMemberId || "",
    consultationTypeName: match.consultationTypeName || "",
  } as GetMemberConsultationsItem;
}

/**
 * Determines if screen order should swap (member selection before service type)
 * Swaps when ALL THREE conditions are met:
 * - User has dependents
 * - No query params (normal entry, not deep link)
 * - Dependent not already selected (prevents re-showing selection)
 *
 * Note: Active consultations no longer block screen swap (Dependents Per Day).
 * When user has dependents, they should always select the member first. Consultation
 * matching happens per-member AFTER member selection, allowing:
 * - Main member to start consultation for dependent even when main member has active consultation
 * - Each dependent to have their own consultation independently
 *
 * MUST be invoked only when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
 *
 * @param context - State machine context with dependents list, query params, and selected dependent
 * @returns True if screen order should swap (show member selection first)
 */
export function shouldSwapScreenOrder(context: Context): boolean {
  const hasQueryParams =
    Boolean(context.queryParamConsultationId) ||
    (context.primarySymptomIds?.length || 0) > 0 ||
    Boolean(context.queryParamDocMedsBundles);

  const hasDependents = (context.memberDependantsList?.length || 0) > 0;
  const dependentAlreadySelected = context.selectedDependent !== null;

  return hasDependents && !hasQueryParams && !dependentAlreadySelected;
}

/**
 * Creates a reusable state entry logger action for XState
 * Reduces duplication by extracting common logging patterns
 *
 * @param stateName - The name of the state being entered
 * @param contextExtractor - Function that extracts relevant context properties to log
 * @returns An XState entry action function
 */
function createStateEntryLogger<TContext>(
  stateName: string,
  contextExtractor: (context: TContext) => Record<string, any>
) {
  return ({ context }: { context: TContext }) => {
    debugLogger.stateEntry(stateName, contextExtractor(context));
  };
}

/**
 * Creates a reusable decision state logger action for XState
 * Similar to createStateEntryLogger but uses debugLogger.decisionState
 *
 * @param stateName - The name of the decision state being entered
 * @param contextExtractor - Function that extracts relevant context properties to log
 * @returns An XState entry action function
 */
function createDecisionStateLogger<TContext>(
  stateName: string,
  contextExtractor: (context: TContext) => Record<string, any>
) {
  return ({ context }: { context: TContext }) => {
    debugLogger.decisionState(stateName, contextExtractor(context));
  };
}

const initialContextValues: Context = {
  activeConsultationListResponse: [],
  memberProfileResponse: null,
  isDoctorMedicineBundleSelected: false,
  membersList: [],
  currentUserMemberId: null,
  memberDependantsList: [],
  isNurseConsultationSelected: false,
  selectedDependent: null,
  queryParamConsultationId: "",
  queryParamDocMedsBundles: false,
  selectedConsultation: null,
  consultationOutcomeResponse: null,
  consultationRatesResponse: null,
  consultationType: MemberConsultationType.SYMPTOM_ASSESSMENT,
  userRating: {
    rating: 0,
    comment: "",
    serviceType: ServiceRating.CONSULTATION,
    ratedServiceId: "",
  },
  consultationAvailabilityData: defaultAvailabilityData,
  startConsultationTime: null,
  isDirectToDoctorSelected: false,
  primarySymptomIds: [],
  memberServiceOfferingUsages: null,
  primarySymptomsResponse: null,
  isPromoOrVoucherCodeApplied: false,
  selectedVoucherOrPromo: null,
  contractsToSign: null,
  oneVoucherPin: undefined,
  oneVoucherRedemptionError: undefined,
  paymentRedirectUrl: undefined,
  paymentReceiptResponse: null,
  isFromOrderSummary: false,
  isFromMyPurchases: false,
  selectedDiscountVoucher: null,
  capturedDiscountVoucherCode: null,
  applyDiscountVoucherResponse: null,
  applyDiscountVoucherResponseError: null,
  isUseVoucherLater: false,
  matchedConsultation: null,
  skipPayment: false,
  detectionError: null,
};

export const eventNames = {
  BACK_BUTTON_CLICKED: "BACK_BUTTON_CLICKED",
  GO_HOME_BUTTON_CLICKED: "GO_HOME_BUTTON_CLICKED",
  NEXT_BUTTON_CLICKED: "NEXT_BUTTON_CLICKED",
  CONTINUE_BUTTON_CLICKED: "CONTINUE_BUTTON_CLICKED",
  MORE_INFO_BUTTON_CLICKED: "MORE_INFO_BUTTON_CLICKED",
  START_CONSULTATION_BUTTON_CLICKED: "START_CONSULTATION_BUTTON_CLICKED",
  DEPENDANT_SELECTED: "DEPENDANT_SELECTED",
  ACTIVE_CONSULTATION_SELECTED: "ACTIVE_CONSULTATION_SELECTED",
  USER_REDIRECTED: "USER_REDIRECTED",
  RATING_TRY_AGAIN: "RATING_TRY_AGAIN",
  DIRECT_TO_DOCTOR_BUTTON_CLICKED: "DIRECT_TO_DOCTOR_BUTTON_CLICKED",
  DOCTOR_MEDICINE_CONSULT_BUTTON_CLICKED:
    "DOCTOR_MEDICINE_CONSULT_BUTTON_CLICKED",
  FIND_A_DOCTOR_BUTTON_CLICKED: "FIND_A_DOCTOR_BUTTON_CLICKED",
  NURSE_CONSULTATION_BUTTON_CLICKED: "NURSE_CONSULTATION_BUTTON_CLICKED",
  SKIP_BUTTON_CLICKED: "SKIP_BUTTON_CLICKED",
  SEE_CONDITIONS_BUTTON_CLICKED: "SEE_CONDITIONS_BUTTON_CLICKED",
  PRIMARY_SYMPTOM_BUTTON_CLICKED: "PRIMARY_SYMPTOM_BUTTON_CLICKED",
  CARD_PAYMENT_BUTTON_CLICKED: "CARD_PAYMENT_BUTTON_CLICKED",
  ONE_VOUCHER_BUTTON_CLICKED: "ONE_VOUCHER_BUTTON_CLICKED",
  VOUCHER_BUTTON_CLICKED: "VOUCHER_BUTTON_CLICKED",
  ONE_VOUCHER_CONTINUE_BUTTON_CLICKED: "ONE_VOUCHER_CONTINUE_BUTTON_CLICKED",
  USER_REQUESTED_PAYMENT: "USER_REQUESTED_PAYMENT",
  VIEW_RECEIPT_BUTTON_CLICKED: "VIEW_RECEIPT_BUTTON_CLICKED",
  RATE_SERVICE_BUTTON_CLICKED: "RATE_SERVICE_BUTTON_CLICKED",
  DISCOUNT_VOUCHER_SELECTED: "DISCOUNT_VOUCHER_SELECTED",
  APPLY_DISCOUNT_VOUCHER: "APPLY_DISCOUNT_VOUCHER",
  ADD_A_DISCOUNT_VOUCHER_CLICKED: "ADD_A_DISCOUNT_VOUCHER_CLICKED",
};

export const intercareFlowStateMachine = setup({
  types: {
    context: {} as Context,
  },
  actions: {
    addActiveConsultationListResponseToContext: assign(({ event }) => {
      return { activeConsultationListResponse: event.output };
    }),
    addMembersProfileResponseToContext: assign(({ event }) => {
      return { memberProfileResponse: event.output };
    }),
    addMembersListToContext: assign(({ event }) => {
      const unu01Membership = findUNU01Membership(event.output.memberships);
      return { membersList: unu01Membership?.members || [] };
    }),
    addMembersDependantsListToContext: assign(({ event }) => {
      return { memberDependantsList: event.output };
    }),
    addMemberServiceOfferingUsageResponseToContext: assign(({ event }) => {
      return { memberServiceOfferingUsages: event.output.body };
    }),
    addPrimarySymptomsResponseToContext: assign(({ event }) => {
      return { primarySymptomsResponse: event.output };
    }),
    addSelectedDependentToContext: assign(({ event }) => {
      return { selectedDependent: event.input };
    }),
    resetSelectedDependent: assign(() => {
      return { selectedDependent: null };
    }),
    addConsultationOutcomeResponseToContext: assign(({ event }) => {
      return { consultationOutcomeResponse: event.input || event.output };
    }),
    addMatchedConsultationToContext: assign(({ event }) => {
      return {
        matchedConsultation: event.output || null,
        detectionError: null,
      };
    }),
    addDetectionErrorToContext: assign(({ event }) => {
      return {
        detectionError: event.error as Error,
        matchedConsultation: null,
      };
    }),
    addSelectedConsultationToContext: assign(({ event }) => {
      return { selectedConsultation: event.input };
    }),
    addIsDirectToDoctorToContext: assign(() => {
      return {
        isDirectToDoctorSelected: true,
        isNurseConsultationSelected: false,
        isDoctorMedicineBundleSelected: false,
      };
    }),
    addIsDoctorMedicineBundleToContext: assign(() => {
      return {
        isDirectToDoctorSelected: false,
        isNurseConsultationSelected: false,
        isDoctorMedicineBundleSelected: true,
      };
    }),
    removeIsDoctorMedicineBundleToContext: assign(() => {
      return { isDoctorMedicineBundleSelected: false };
    }),
    addIsNurseConsultationToContext: assign(() => {
      return {
        isNurseConsultationSelected: true,
        isDirectToDoctorSelected: false,
        isDoctorMedicineBundleSelected: false,
      };
    }),
    removeIsDirectToDoctorToContext: assign(() => {
      return { isDirectToDoctorSelected: false };
    }),
    removeIsNurseConsultationToContext: assign(() => {
      return { isNurseConsultationSelected: false };
    }),
    addStartConsultationResponseToContext: assign(({ event }) => {
      return { selectedConsultation: event.input || event.output };
    }),
    addUserRatingToContext: assign(({ event }) => {
      return { userRating: event.input || event.output };
    }),
    addConsultationRatesResponseToContext: assign(({ event }) => {
      return { consultationRatesResponse: event.output };
    }),
    addIsPromoOrVoucherCodeAppliedToContext: assign(() => {
      return { isPromoOrVoucherCodeApplied: true };
    }),
    addPrimarySymptomIdsToContext: assign(({ event }) => {
      return { primarySymptomIds: [event.input] };
    }),
    addSelectedVoucherOrPromoToContext: assign(({ event }) => {
      return { selectedVoucherOrPromo: event.input };
    }),
    addContractsToSignToContext: assign(({ event }) => {
      return { contractsToSign: event.output };
    }),
    addOneVoucherPinToContext: assign(({ event }) => {
      return {
        oneVoucherPin: event.input,
      };
    }),
    addInitiatePaymentResponseErrorToContext: assign(({ event }) => {
      return {
        oneVoucherRedemptionError: event.error,
      };
    }),
    clearOneVoucherRedemptionResponseAndPin: assign(() => {
      return {
        oneVoucherRedemptionError: null,
        oneVoucherPin: undefined,
      };
    }),
    clearSelectedDiscountVoucher: assign(() => {
      return {
        selectedDiscountVoucher: null,
      };
    }),
    addPaymentRedirectUrlToContext: assign(({ event }) => {
      return { paymentRedirectUrl: event.url };
    }),
    redirectToPaymentPage: ({ context }) => {
      if (context.paymentRedirectUrl) {
        window.open(context.paymentRedirectUrl, "_blank");
      }
    },
    addPaymentReceiptResponseToContext: assign(({ event }) => {
      return {
        paymentReceiptResponse: event.output,
      };
    }),
    addIsFromOrderSummaryToContext: assign(() => {
      return { isFromOrderSummary: true };
    }),
    addSelectedDiscountVoucherToContext: assign(({ event }) => {
      return {
        selectedDiscountVoucher: event.input || null,
        isUseVoucherLater: false,
      };
    }),
    addCapturedDiscountVoucherCodeToContext: assign(({ event }) => {
      return {
        capturedDiscountVoucherCode: event.input.voucherCode || null,
        isUseVoucherLater: event.input.isUseVoucherLater || false,
      };
    }),
    addApplyDiscountVoucherResponseToContext: assign(({ event }) => {
      return {
        applyDiscountVoucherResponse: event.output || null,
        selectedDiscountVoucher: event.output.memberOfferingUsage || null,
      };
    }),
    addApplyDiscountVoucherResponseErrorToContext: assign(({ event }) => {
      return { applyDiscountVoucherResponseError: event.error || null };
    }),
    removeApplyDiscountVoucherResponseErrorFromContext: assign(() => {
      return {
        applyDiscountVoucherResponseError: null,
        capturedDiscountVoucherCode: null,
        isUseVoucherLater: false,
      };
    }),
    clearCapturedDiscountVoucher: assign(() => {
      return {
        applyDiscountVoucherResponseError: null,
        applyDiscountVoucherResponse: null,
        capturedDiscountVoucherCode: null,
      };
    }),
    clearOnApplyDiscountVoucher: assign(() => {
      return {
        applyDiscountVoucherResponseError: null,
        applyDiscountVoucherResponse: null,
        selectedDiscountVoucher: null,
      };
    }),
  },
  actors: {
    getActiveConsultations: fromPromise(async () => {
      return await getMemberConsultations();
    }),
    /**
     * Find existing consultation by memberId and chargeCode
     * Used for Consultation Idempotency detection
     * Only invoked when VITE_APP_FEATURE_ENABLE_SINGLE_CONSULTATION_ID toggle is enabled
     */
    findExistingConsultation: fromPromise(
      async ({
        input,
      }: {
        input: { context: Context };
      }): Promise<ConsultationMatch | null> => {
        const { context } = input;

        const memberId =
          context.selectedDependent?.memberId || context.currentUserMemberId;

        const chargeCode = getChargeCodeForConsultationType(context);

        if (!memberId || !chargeCode) {
          return null;
        }
        const consultations = await getMemberConsultations(
          GetMemberProductPaymentRatesStatus.ACTIVE
        );

        const consultationsList = Array.isArray(consultations)
          ? consultations
          : [];

        const matchedConsultation = consultationsList.find(
          (c) => c.memberId === memberId && c.chargeCode === chargeCode
        );

        if (!matchedConsultation) {
          return null;
        }

        const consultationMatch: ConsultationMatch = {
          consultationId: matchedConsultation.consultationId,
          memberId: matchedConsultation.memberId,
          chargeCode: matchedConsultation.chargeCode,
          outcome: matchedConsultation.outcome || "",
          redirectUrl: matchedConsultation.redirectUrl || null,
          status: matchedConsultation.status || "",
          startedOn: matchedConsultation.startedOn,
          chargeAmountFormatted:
            matchedConsultation.chargeAmountFormatted || null,
          serviceProviderName: matchedConsultation.serviceProviderName || null,
          mainMemberId: matchedConsultation.mainMemberId || null,
          isDependant: matchedConsultation.isDependant,
          consultationTypeName:
            matchedConsultation.consultationTypeName || undefined,
        };

        return consultationMatch;
      }
    ),
    fetchingPrimarySymptoms: fromPromise(async () => {
      return await getDirectToDoctorPrimarySymptoms(Status.ACTIVE);
    }),
    getMemberships: fromPromise(async () => {
      return await getMemberships();
    }),
    getMemberDependants: fromPromise(async () => {
      return await getMembershipDependents();
    }),
    getMemberProfile: fromPromise(async () => {
      return await getMemberProfile();
    }),
    getMemberServiceOfferingUsage: fromPromise(async () => {
      return await getMemberServiceOfferingUsage(
        MemberServiceOfferingType.CONSULTATIONS
      );
    }),
    getConsultationOutcome: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const consultationId =
          input.context.selectedConsultation?.consultationId ||
          input.context.queryParamConsultationId;
        return await getMemberConsultationOutcome(consultationId);
      }
    ),
    getMemberRequestPaymentOutcome: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const consultationId =
          input.context.selectedConsultation?.consultationId ||
          input.context.queryParamConsultationId;
        return await memberRequestPayment(consultationId);
      }
    ),
    getMemberConsultationRates: fromPromise(async () => {
      return await getMemberProductPaymentRates();
    }),
    getContractsToSign: fromPromise(async () => {
      return await getContractsToSign();
    }),
    startConsultation: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        const isPromoCodesAndVoucheringFeatureEnabled =
          isFeatureEnabled(FeatureToggleIdentifier.ENABLE_PROMO_CODES) &&
          isFeatureEnabled(FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION);

        const isDiscountVouchersFeatureEnabled = isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS
        );

        const selectedDiscountVoucher = isDiscountVouchersFeatureEnabled
          ? input.context.selectedDiscountVoucher
          : null;

        const { isPromoOrVoucherCodeApplied } = input.context;
        const selectedVoucherOrPromoId =
          input.context?.selectedVoucherOrPromo?.id || "";
        const isEnhancedPaymentOptionsEnabled = isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
        );
        const oneVoucherPin = input.context.oneVoucherPin;

        const selectedMemberId =
          input.context.selectedDependent?.memberId ||
          input.context.membersList.find(
            (member) =>
              member.membershipDetails.type === MembershipType.MAIN_MEMBER
          )?.memberDetails?.id ||
          "";

        let consultationType: MemberConsultationType =
          input.context.consultationType;

        let offeringId: string =
          input.context.memberProfileResponse?.consultation?.consultationRates.find(
            (rate) =>
              rate.chargeCode ===
              MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
          )?.offeringId || "";
        let primarySymptomIds: string[] = [];

        if (input.context.isDirectToDoctorSelected) {
          consultationType = MemberConsultationType.DIGITAL_DOCTOR_CONSULT;
          offeringId =
            input.context.memberProfileResponse?.consultation?.consultationRates.find(
              (rate) =>
                rate.chargeCode ===
                MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
            )?.offeringId || "";
        }

        if (input.context.isDoctorMedicineBundleSelected) {
          consultationType =
            MemberConsultationType.DIGITAL_DOCTOR_MEDICINE_BUNDLE_CONSULT;
          offeringId =
            input.context.memberProfileResponse?.consultation?.consultationRates.find(
              (rate) =>
                rate.chargeCode ===
                MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
            )?.offeringId || "";
        }

        if (input.context.primarySymptomIds) {
          primarySymptomIds = input.context.primarySymptomIds;
        }

        if (isEnhancedPaymentOptionsEnabled && !!oneVoucherPin) {
          return await startMemberConsultation(
            consultationType,
            selectedMemberId,
            offeringId,
            primarySymptomIds,
            !!selectedDiscountVoucher ? selectedDiscountVoucher.id : undefined,
            undefined,
            oneVoucherPin
          );
        }

        if (
          isPromoCodesAndVoucheringFeatureEnabled &&
          isPromoOrVoucherCodeApplied &&
          selectedVoucherOrPromoId
        ) {
          return await startMemberConsultation(
            consultationType,
            selectedMemberId,
            offeringId,
            primarySymptomIds,
            selectedVoucherOrPromoId,
            isPromoOrVoucherCodeApplied
          );
        }
        return await startMemberConsultation(
          consultationType,
          selectedMemberId,
          offeringId,
          primarySymptomIds,
          !!selectedDiscountVoucher ? selectedDiscountVoucher.id : undefined
        );
      }
    ),
    updateMemberDependents: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return updateMembershipDependents({
          dependents: input.context.selectedDependent
            ? [input.context.selectedDependent]
            : [],
        });
      }
    ),
    submitRating: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return await submitRating({
          rating: input.context.userRating.rating,
          comment: input.context.userRating?.comment || "",
          serviceType:
            input.context.userRating?.serviceType || ServiceRating.CONSULTATION,
          ratedServiceId:
            input.context.selectedConsultation?.consultationId ||
            input.context.queryParamConsultationId,
        });
      }
    ),
    fetchPaymentReceipt: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        if (!input.context.queryParamConsultationId) return null;
        else
          return getConsultationPaymentReceipt(
            input?.context?.queryParamConsultationId
          );
      }
    ),
    applyDiscountVoucher: fromPromise(
      async ({ input }: { input: { context: Context } }) => {
        return await applyDiscountVoucher(
          input.context.capturedDiscountVoucherCode || "",
          input.context.currentUserMemberId || ""
        );
      }
    ),
  },
  guards: {
    hasConsultationIdInQueryParam: function ({ context }) {
      return !!context.queryParamConsultationId;
    },
    hasDocMedsBundlesInQueryParam: function ({ context }) {
      return context.queryParamDocMedsBundles !== false;
    },
    hasPaymentSucceededAndIsDirectToDoctor: function ({ context }) {
      return Boolean(
        context.consultationOutcomeResponse?.outcome ===
          MemberConsultationOutcome.PAYMENT_SUCCEEDED &&
          context.isDirectToDoctorSelected
      );
    },
    isOutsideOperatingHours: function ({ context }) {
      const currentTime = getCurrentFormattedTime();
      const currentDay = getCurrentFormattedDay();

      const now = new Date();

      const isChristmas = now.getDate() === 25 && now.getMonth() === 11;
      const isNewYearsDay = now.getDate() === 1 && now.getMonth() === 0;
      if (isChristmas || isNewYearsDay) {
        return true;
      }
      const isNewYearsEve = now.getMonth() === 11 && now.getDate() === 31;
      if (isNewYearsEve) {
        return currentTime < "07:00" || currentTime > "12:45";
      }
      const isChristmasEve = now.getMonth() === 11 && now.getDate() === 24;
      if (isChristmasEve) {
        return currentTime < "07:00" || currentTime > "12:45";
      }

      try {
        if (isPublicHolidayInSouthAfrica()) {
          return currentTime < "09:00" || currentTime > "12:45";
        }
      } catch (error) {
        console.error("Error checking for public holiday:", error);
      }

      const operatingHours = context.consultationAvailabilityData.find(
        (day) => day.day === currentDay
      );
      const [openingTime, closingTime] = operatingHours?.operatingHours.split(
        " - "
      ) as any;

      const isOutsideOperatingHours =
        currentTime < openingTime || currentTime > closingTime;
      return isOutsideOperatingHours;
    },
    hasNoActiveConsultationsAndIsOutsideOperatingHours: function ({ context }) {
      const hasActiveConsultations =
        context.activeConsultationListResponse.length > 0;

      const currentTime = getCurrentFormattedTime();
      const currentDay = getCurrentFormattedDay();

      const operatingHours = context.consultationAvailabilityData.find(
        (day) => day.day === currentDay
      );
      const [openingTime, closingTime] = operatingHours?.operatingHours.split(
        " - "
      ) as any;

      const isOutsideOperatingHours =
        currentTime < openingTime || currentTime > closingTime;
      return isOutsideOperatingHours && !hasActiveConsultations;
    },
    hasAtLeastOneActiveConsultations: function ({ context }) {
      return context.activeConsultationListResponse.length > 0;
    },
    hasDependentsAndNoActiveConsultations: function ({ context }) {
      return (
        context.activeConsultationListResponse.length === 0 &&
        context.memberDependantsList.length > 0
      );
    },
    hasNoDependentsAndNoActiveConsultations: function ({ context }) {
      return (
        context.activeConsultationListResponse.length === 0 &&
        context.memberDependantsList.length === 0
      );
    },
    hasActiveConsultationsAndIsOutsideOperatingHours: function ({ context }) {
      const hasActiveConsultations =
        context.activeConsultationListResponse.length > 0;

      const currentTime = getCurrentFormattedTime();
      const currentDay = getCurrentFormattedDay();

      const operatingHours = context.consultationAvailabilityData.find(
        (day) => day.day === currentDay
      );
      const [openingTime, closingTime] = operatingHours?.operatingHours.split(
        " - "
      ) as any;

      return (
        (currentTime <= openingTime || currentTime >= closingTime) &&
        hasActiveConsultations
      );
    },
    hasDependents: function ({ context }) {
      const result = (context.memberDependantsList?.length || 0) > 0;
      debugLogger.guard("hasDependents", {
        result,
        dependentsCount: context.memberDependantsList?.length || 0,
        selectedDependent: context.selectedDependent,
        activeConsultationsCount:
          context.activeConsultationListResponse?.length || 0,
        hasActiveConsultations:
          (context.activeConsultationListResponse?.length || 0) > 0,
      });
      return result;
    },
    shouldEnableDependentBackNavigation: function ({ context }) {
      const isIdempotencyEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_SINGLE_CONSULTATION_ID
      );
      const hasDependents = (context.memberDependantsList?.length || 0) > 0;
      const result = isIdempotencyEnabled && hasDependents;
      debugLogger.guard("shouldEnableDependentBackNavigation", {
        result,
        isIdempotencyEnabled,
        hasDependents,
        dependentsCount: context.memberDependantsList?.length || 0,
      });
      return result;
    },
    shouldShowDependentSelection: function ({ context }) {
      const hasDependentsList = (context.memberDependantsList?.length || 0) > 0;
      const noDependentSelected = context.selectedDependent === null;
      const result = hasDependentsList && noDependentSelected;

      debugLogger.guard("shouldShowDependentSelection", {
        result,
        hasDependentsList,
        noDependentSelected,
        dependentsCount: context.memberDependantsList?.length || 0,
        selectedDependent: context.selectedDependent,
        activeConsultationsCount:
          context.activeConsultationListResponse?.length || 0,
        logic: `${hasDependentsList} (has dependents) && ${noDependentSelected} (none selected) = ${result}`,
      });

      return result;
    },
    selectedConsultationHasOutstandingPayment: function ({ context }) {
      const { chargeAmountFormatted, chargeCode, outcome } =
        context.selectedConsultation as any;

      return Boolean(
        (outcome === MemberConsultationOutcome.PAYMENT_REQUESTED ||
          outcome === MemberConsultationOutcome.PAYMENT_FAILED) &&
          chargeAmountFormatted &&
          chargeCode
      );
    },
    selectedConsultationHasOutstandingRefund: function ({ context }) {
      return (
        context.selectedConsultation?.outcome ===
        MemberConsultationOutcome.REFUND_REQUESTED
      );
    },
    selectedConsultationHasOutstandingPaymentWithNoCharge: function ({
      context,
    }) {
      const { chargeAmountFormatted, chargeCode, outcome } =
        context.selectedConsultation as any;
      return Boolean(
        (outcome === MemberConsultationOutcome.PAYMENT_REQUESTED ||
          outcome === MemberConsultationOutcome.PAYMENT_FAILED) &&
          (!chargeAmountFormatted || !chargeCode)
      );
    },
    userHasFindAHospitalOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.EMERGENCY_CONSULT
      );
    },
    userHasArchivedConsultation: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.status ===
        MemberConsultationStatus.ARCHIVED
      );
    },
    userHasUnsuccessfulPaymentOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.PAYMENT_FAILED
      );
    },
    userHasFindADoctorOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.INPERSON_CONSULT
      );
    },
    activeUserHasCompletedOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.DIGITAL_CONSULT_ENDED
      );
    },
    selectedConsultationHasNoRefund: function ({ context }) {
      return (
        context.selectedConsultation?.outcome ===
        MemberConsultationOutcome.NO_REFUND
      );
    },
    hasActiveConsultations: function ({ context }) {
      return context.activeConsultationListResponse.length > 0;
    },
    userHasCompletedOutcome: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
          MemberConsultationOutcome.DIGITAL_CONSULT_ENDED ||
        context.consultationOutcomeResponse?.outcome ===
          MemberConsultationOutcome.NO_REFUND
      );
    },
    userHasOutstandingRefund: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.REFUND_REQUESTED
      );
    },
    timeoutExceeded: function ({ context }) {
      const currentDate = new Date();
      const consultationStartTime = context.startConsultationTime;

      if (consultationStartTime) {
        return (
          Number(currentDate.getTime() - consultationStartTime.getTime()) /
            1000 >
          MAX_POLLING_TIME_IN_SECONDS
        );
      }

      return false;
    },
    userHasNotMadePayment: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.PAYMENT_REQUESTED
      );
    },
    userHasMadePayment: function ({ context }) {
      return (
        context.consultationOutcomeResponse?.outcome ===
        MemberConsultationOutcome.PAYMENT_SUCCEEDED
      );
    },
    hasNoActiveConsultations: function ({ context }) {
      return context.activeConsultationListResponse.length === 0;
    },
    isDirectToDoctorSelected: function ({ context }) {
      return context.isDirectToDoctorSelected;
    },
    isDoctorMedicineBundleSelected: function ({ context }) {
      return context.isDoctorMedicineBundleSelected;
    },
    isNurseConsultationSelected: function ({ context }) {
      return context.isNurseConsultationSelected;
    },
    hasNoConsultationTypeSelected: function ({ context }) {
      return (
        !context.isDirectToDoctorSelected &&
        !context.isDoctorMedicineBundleSelected &&
        !context.isNurseConsultationSelected
      );
    },
    isSelectedDependentNotMainMember: function ({ context }) {
      return (
        context.selectedDependent?.memberId !== context.currentUserMemberId &&
        isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_DEPENDANT_SERVICE_OFFERING
        )
      );
    },
    hasPrimarySymptoms: function ({ context }) {
      return context.primarySymptomIds.length > 0;
    },
    shouldSwapScreenOrderForDependents: function ({ context }) {
      const isIdempotencyEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_SINGLE_CONSULTATION_ID
      );

      if (!isIdempotencyEnabled) {
        debugLogger.guard("shouldSwapScreenOrderForDependents", {
          result: false,
          reason: "idempotency toggle disabled",
        });
        return false;
      }

      const result = shouldSwapScreenOrder(context);
      debugLogger.guard("shouldSwapScreenOrderForDependents", {
        result,
        hasDependents: context.memberDependantsList?.length > 0,
        dependentsCount: context.memberDependantsList?.length,
        hasQueryParams:
          Boolean(context.queryParamConsultationId) ||
          (context.primarySymptomIds?.length || 0) > 0 ||
          Boolean(context.queryParamDocMedsBundles),
        queryParamConsultationId: context.queryParamConsultationId,
        primarySymptomIds: context.primarySymptomIds,
        queryParamDocMedsBundles: context.queryParamDocMedsBundles,
        dependentAlreadySelected: context.selectedDependent !== null,
        selectedDependent: context.selectedDependent,
        isDirectToDoctorSelected: context.isDirectToDoctorSelected,
        isNurseConsultationSelected: context.isNurseConsultationSelected,
      });
      return result;
    },
    shouldEnterMatchingState: function () {
      const isIdempotencyEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_SINGLE_CONSULTATION_ID
      );

      return isIdempotencyEnabled;
    },
    shouldSkipActiveConsultationsScreen: function () {
      return isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_SINGLE_CONSULTATION_ID
      );
    },
    hasMatchedConsultationWithPaymentRequested: function ({ context }) {
      const { matchedConsultation } = context;
      if (!matchedConsultation) return false;
      return (
        getConsultationRoute(matchedConsultation) ===
        ConsultationRoute.PAYMENT_FLOW
      );
    },
    hasMatchedConsultationWithPaymentSucceeded: function ({ context }) {
      const { matchedConsultation } = context;
      if (!matchedConsultation) return false;
      return (
        getConsultationRoute(matchedConsultation) ===
        ConsultationRoute.INTERCARE_LINK
      );
    },
    hasMatchedConsultationWithUnknownOutcome: function ({ context }) {
      const { matchedConsultation } = context;
      if (!matchedConsultation) return false;
      return (
        getConsultationRoute(matchedConsultation) ===
        ConsultationRoute.DEFER_TO_WORKFLOW
      );
    },
    isPromoCodeFeatureEnabled: function () {
      const isPromoCodesFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_PROMO_CODES
      );

      return isPromoCodesFeatureEnabled;
    },
    isEnhancedPaymentOptionsEnabled: function () {
      return isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );
    },
    isEnhancedPaymentOptionsEnabledAndPaymentNotSkipped: function ({
      context,
    }) {
      const isEnhancedPaymentOptionsEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );
      return isEnhancedPaymentOptionsEnabled && !context.skipPayment;
    },
    isEnhancedPaymentOptionsEnabledAndHasOneVoucherPinInContext: function ({
      context,
    }) {
      const isEnhancedPaymentOptionsEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );
      const hasOneVoucherPinInContext = !!context.oneVoucherPin;

      return Boolean(
        isEnhancedPaymentOptionsEnabled && hasOneVoucherPinInContext
      );
    },
    hasValidDoctorPromoCodeAndIsEnhancedPaymentsDisabled: function ({
      context,
    }) {
      const isPromoCodesFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_PROMO_CODES
      );

      const isVoucheringFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION
      );

      const isEnhancedPaymentOptionsEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );

      let hasValidPromoCode = false;

      if (context.memberServiceOfferingUsages !== null) {
        const currentDate = new Date();

        if (isVoucheringFeatureEnabled && !isEnhancedPaymentOptionsEnabled) {
          hasValidPromoCode = context.memberServiceOfferingUsages
            .filter(
              (usage) =>
                usage.chargeCode ===
                MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_CONSULT
            )
            .some((promo) => {
              const availableFrom = new Date(promo.availableFrom);
              const availableTo = new Date(promo.availableTo);

              return (
                promo.usageStatus === MemberServiceOfferingStatus.ACTIVE &&
                currentDate >= availableFrom &&
                currentDate <= availableTo
              );
            });
        } else {
          hasValidPromoCode = context.memberServiceOfferingUsages.some(
            (promo) => {
              const availableFrom = new Date(promo.availableFrom);
              const availableTo = new Date(promo.availableTo);

              return (
                promo.usageStatus === MemberServiceOfferingStatus.ACTIVE &&
                currentDate >= availableFrom &&
                currentDate <= availableTo
              );
            }
          );
        }
      }
      return (
        isPromoCodesFeatureEnabled &&
        hasValidPromoCode &&
        !isEnhancedPaymentOptionsEnabled
      );
    },
    isPromoCodeAndVoucherFeatureEnabledAndHasValidNursePromoCode: function ({
      context,
    }) {
      const isPromoCodesFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_PROMO_CODES
      );

      const isVoucheringFeatureEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_VOUCHER_REDEMPTION
      );

      const isEnhancedPaymentOptionsEnabled = isFeatureEnabled(
        FeatureToggleIdentifier.ENABLE_ENHANCED_PAYMENT_OPTIONS
      );

      let hasValidPromoCode = false;

      if (context.memberServiceOfferingUsages !== null) {
        const currentDate = new Date();

        if (isVoucheringFeatureEnabled && !isEnhancedPaymentOptionsEnabled) {
          hasValidPromoCode = context.memberServiceOfferingUsages
            .filter(
              (usage) =>
                usage.chargeCode ===
                MemberProductPaymentRatesChargeCode.ONLINE_DOCTOR_CONSULTATION
            )
            .some((promo) => {
              const availableFrom = new Date(promo.availableFrom);
              const availableTo = new Date(promo.availableTo);

              return (
                promo.usageStatus === MemberServiceOfferingStatus.ACTIVE &&
                currentDate >= availableFrom &&
                currentDate <= availableTo
              );
            });
        }
      }
      return (
        isPromoCodesFeatureEnabled &&
        hasValidPromoCode &&
        !isEnhancedPaymentOptionsEnabled
      );
    },
    isPromoCodeAndVoucherFeatureEnabledAndHasValidDocMedBundlePromoCode:
      function ({ context }) {
        const isPromoCodesFeatureEnabled = isFeatureEnabled(
          FeatureToggleIdentifier.ENABLE_PROMO_CODES
        );

        let hasValidPromoCode = false;

        if (context.memberServiceOfferingUsages !== null) {
          const currentDate = new Date();

          hasValidPromoCode = context.memberServiceOfferingUsages
            .filter(
              (usage) =>
                usage.chargeCode ===
                MemberProductPaymentRatesChargeCode.DIGITAL_DOCTOR_MEDICINE_CONSULT
            )
            .some((promo) => {
              const availableFrom = new Date(promo.availableFrom);
              const availableTo = new Date(promo.availableTo);

              return (
                promo.usageStatus === MemberServiceOfferingStatus.ACTIVE &&
                currentDate >= availableFrom &&
                currentDate <= availableTo
              );
            });
        }
        return isPromoCodesFeatureEnabled && hasValidPromoCode;
      },
    hasPaymentReceiptResponse: function ({ context }) {
      return Boolean(context.paymentReceiptResponse);
    },
    isFromMyPurchases: function ({ context }) {
      return (
        Boolean(context.queryParamConsultationId) &&
        Boolean(context.isFromMyPurchases)
      );
    },
    isDiscountVouchersFeatureEnabled: function () {
      return isFeatureEnabled(FeatureToggleIdentifier.ENABLE_DISCOUNT_VOUCHERS);
    },
  },
  schemas: {},
  delays: {
    CONSULTATION_INTERVAL: POLLING_INTERVAL_IN_MILLISECONDS,
  },
}).createMachine({
  context: initialContextValues,
  id: "intercareFlow",
  initial: "start",
  states: {
    start: {
      entry: assign(({ event }) => {
        const entryContext = { ...initialContextValues };
        const {
          primarySymptomIds = [],
          queryParamConsultationId = "",
          queryParamDocMedsBundles = false,
          currentUserMemberId = null,
          isFromMyPurchases = false,
        } = event.input?.context ? event.input.context : {};
        return {
          ...entryContext,
          queryParamConsultationId,
          queryParamDocMedsBundles,
          primarySymptomIds,
          currentUserMemberId,
          isFromMyPurchases,
        };
      }),
      invoke: {
        id: "start",
        input: ({ context }) => ({ context }),
        onDone: [
          {
            target: "fetchingPaymentReceipt",
            guard: {
              type: "isFromMyPurchases",
            },
          },
          {
            target: "fetchingMemberProfile",
            actions: {
              type: "addActiveConsultationListResponseToContext",
            },
          },
        ],
        onError: {
          target: "getActiveConsultationsError",
        },
        src: "getActiveConsultations",
      },
    },
    fetchingPaymentReceipt: {
      invoke: {
        id: "fetchPaymentReceipt",
        input: ({ context }) => ({ context }),
        onDone: [
          {
            target: "showingOrderSummary",
            actions: {
              type: "addPaymentReceiptResponseToContext",
            },
          },
          {
            target: "exitToMyPurchases",
            guard: {
              type: "hasConsultationIdInQueryParam",
            },
          },
          {
            target: "exit",
          },
        ],
        onError: {
          target: "genericError",
        },
        src: "fetchPaymentReceipt",
      },
    },
    showingOrderSummary: {
      always: [
        {
          target: "showOrderSummaryScreen",
          guard: {
            type: "hasPaymentReceiptResponse",
          },
        },
        {
          target: "exitToMyPurchases",
          guard: {
            type: "hasConsultationIdInQueryParam",
          },
        },
        {
          target: "exit",
        },
      ],
    },
    showOrderSummaryScreen: {
      on: {
        [eventNames.VIEW_RECEIPT_BUTTON_CLICKED]: {
          target: "showingPaymentReceipt",
        },
        [eventNames.RATE_SERVICE_BUTTON_CLICKED]: {
          target: "showingServiceRating",
          actions: {
            type: "addIsFromOrderSummaryToContext",
          },
        },
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "exitToMyPurchases",
            guard: {
              type: "hasConsultationIdInQueryParam",
            },
          },
          {
            target: "exit",
          },
        ],
      },
    },
    showingServiceRating: {
      always: {
        target: "serviceRating",
        guard: { type: "hasPaymentReceiptResponse" },
      },
    },
    showingPaymentReceipt: {
      on: {
        BACK_BUTTON_CLICKED: {
          target: "showingOrderSummary",
        },
      },
    },
    exitToMyPurchases: {
      type: "final",
    },
    fetchingMemberProfile: {
      invoke: {
        id: "fetchingMemberProfile",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingMemberDetails",
          actions: {
            type: "addMembersProfileResponseToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getMemberProfile",
      },
    },
    fetchingMemberDetails: {
      invoke: {
        id: "fetchingMemberDetails",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingMemberDependants",
          actions: {
            type: "addMembersListToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getMemberships",
      },
    },
    fetchingMemberDependants: {
      invoke: {
        id: "fetchingMemberDependants",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "fetchingMemberServiceOfferingUsage",
          actions: {
            type: "addMembersDependantsListToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getMemberDependants",
      },
    },
    fetchingMemberServiceOfferingUsage: {
      invoke: {
        id: "fetchingMemberServiceOfferingUsage",
        input: ({ context }) => ({ context }),
        onDone: [
          {
            target: "fetchingPaymentReceipt",
            guard: {
              type: "isFromMyPurchases",
            },
          },
          {
            target: "selectConsultationTypeOrDefault",
            actions: {
              type: "addMemberServiceOfferingUsageResponseToContext",
            },
          },
        ],
        onError: {
          target: "selectConsultationTypeOrDefault",
        },
        src: "getMemberServiceOfferingUsage",
      },
    },
    selectConsultationTypeOrDefault: {
      always: [
        {
          target: "fetchingConsultationOutcome",
          guard: {
            type: "hasConsultationIdInQueryParam",
          },
        },
        {
          target:
            "promptingDirectToDoctorNextStepsWithPromoCodeOrSelectingDependents",
          guard: {
            type: "hasPrimarySymptoms",
          },
          actions: { type: "addIsDirectToDoctorToContext" },
        },
        {
          target: "showingDocMedBundlesInfo",
          guard: {
            type: "hasDocMedsBundlesInQueryParam",
          },
          actions: {
            type: "addIsDoctorMedicineBundleToContext",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "shouldSwapScreenOrderForDependents",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "hasNoActiveConsultations",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "shouldSkipActiveConsultationsScreen",
          },
        },
        {
          target: "selectingActiveConsultationOrStartingNewConsultation",
        },
      ],
    },
    selectConsultationType: {
      entry: createStateEntryLogger<Context>(
        "selectConsultationType",
        (context) => ({
          selectedDependent: context.selectedDependent,
          dependentsCount: context.memberDependantsList?.length,
          isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          isNurseConsultationSelected: context.isNurseConsultationSelected,
        })
      ),
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "selectingDependents",
            guard: { type: "shouldEnableDependentBackNavigation" },
          },
          {
            target: "exit",
          },
        ],
        [eventNames.DIRECT_TO_DOCTOR_BUTTON_CLICKED]: [
          {
            target: "promptingPromoCode",
            actions: [{ type: "addIsDirectToDoctorToContext" }],
            guard: {
              type: "hasValidDoctorPromoCodeAndIsEnhancedPaymentsDisabled",
            },
          },
          {
            target: "selectingDependents",
            actions: [{ type: "addIsDirectToDoctorToContext" }],
            guard: {
              type: "shouldSwapScreenOrderForDependents",
            },
          },
          {
            target: "promptingDirectToDoctorNextStepsOrSelectingDependents",
            actions: [{ type: "addIsDirectToDoctorToContext" }],
          },
        ],
        [eventNames.NURSE_CONSULTATION_BUTTON_CLICKED]: [
          {
            target: "promptingPromoCode",
            actions: [{ type: "addIsNurseConsultationToContext" }],
            guard: {
              type: "isPromoCodeAndVoucherFeatureEnabledAndHasValidNursePromoCode",
            },
          },
          {
            target: "selectingDependents",
            actions: [{ type: "addIsNurseConsultationToContext" }],
            guard: {
              type: "shouldSwapScreenOrderForDependents",
            },
          },
          {
            target: "promptingNurseConsultNextStepsOrSelectingDependents",
            actions: [{ type: "addIsNurseConsultationToContext" }],
          },
        ],
        [eventNames.DOCTOR_MEDICINE_CONSULT_BUTTON_CLICKED]: [
          {
            target: "promptingPromoCode",
            actions: [{ type: "addIsDoctorMedicineBundleToContext" }],
            guard: {
              type: "isPromoCodeAndVoucherFeatureEnabledAndHasValidDocMedBundlePromoCode",
            },
          },
          {
            target: "selectingDependents",
            actions: [{ type: "addIsDoctorMedicineBundleToContext" }],
            guard: {
              type: "shouldSwapScreenOrderForDependents",
            },
          },
          {
            target: "showingDocMedBundlesInfo",
            actions: [{ type: "addIsDoctorMedicineBundleToContext" }],
          },
        ],
        [eventNames.SEE_CONDITIONS_BUTTON_CLICKED]: {
          target: "fetchingPrimarySymptoms",
        },
      },
    },
    showingDocMedBundlesInfo: {
      invoke: {
        id: "showingDocMedBundlesInfo",
        input: ({ context }) => ({ context }),
        onDone: {
          actions: {
            type: "addContractsToSignToContext",
          },
        },
        onError: {
          target: "showingDocMedBundlesInfo",
        },
        src: "getContractsToSign",
      },
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "exit",
            guard: {
              type: "hasDocMedsBundlesInQueryParam",
            },
          },
          {
            target: "selectConsultationType",
            actions: [{ type: "removeIsDoctorMedicineBundleToContext" }],
          },
        ],

        [eventNames.CONTINUE_BUTTON_CLICKED]: { target: "showingDeliveryInfo" },
      },
    },
    showingDeliveryInfo: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingDocMedBundlesInfo",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "startingConsultation",
          actions: "addSelectedDependentToContext",
        },
      },
    },
    fetchingPrimarySymptoms: {
      invoke: {
        id: "showPrimarySymptomsList",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showPrimarySymptomsList",
          actions: {
            type: "addPrimarySymptomsResponseToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "fetchingPrimarySymptoms",
      },
    },
    showPrimarySymptomsList: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "selectConsultationType",
          actions: [{ type: "removeIsDirectToDoctorToContext" }],
        },
        [eventNames.PRIMARY_SYMPTOM_BUTTON_CLICKED]: [
          {
            target: "promptingPromoCode",
            guard: {
              type: "hasValidDoctorPromoCodeAndIsEnhancedPaymentsDisabled",
            },
            actions: [
              { type: "addPrimarySymptomIdsToContext" },
              { type: "addIsDirectToDoctorToContext" },
            ],
          },
          {
            target: "promptingDirectToDoctorNextStepsOrSelectingDependents",
            actions: [
              { type: "addIsDirectToDoctorToContext" },
              { type: "addPrimarySymptomIdsToContext" },
            ],
          },
        ],
      },
    },
    promptingPromoCode: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "selectConsultationType",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: [
          {
            target: "promptingDirectToDoctorNextStepsOrSelectingDependents",
            guard: { type: "isDirectToDoctorSelected" },
            actions: [
              { type: "addIsPromoOrVoucherCodeAppliedToContext" },
              { type: "addIsDirectToDoctorToContext" },
              { type: "addSelectedVoucherOrPromoToContext" },
            ],
          },
          {
            target: "promptingNurseConsultNextStepsOrSelectingDependents",
            guard: { type: "isNurseConsultationSelected" },
            actions: [
              { type: "addIsPromoOrVoucherCodeAppliedToContext" },
              { type: "addIsNurseConsultationToContext" },
              { type: "addSelectedVoucherOrPromoToContext" },
            ],
          },
        ],
        [eventNames.SKIP_BUTTON_CLICKED]: {
          target: "promptingDirectToDoctorNextStepsOrSelectingDependents",
          actions: [{ type: "addIsDirectToDoctorToContext" }],
        },
      },
    },
    promptingNurseConsultNextStepsOrSelectingDependents: {
      entry: createDecisionStateLogger<Context>(
        "promptingNurseConsultNextStepsOrSelectingDependents",
        (context) => ({
          hasDependents: context.memberDependantsList?.length > 0,
          dependentsCount: context.memberDependantsList?.length,
          selectedDependent: context.selectedDependent,
          isNurseConsultationSelected: context.isNurseConsultationSelected,
          activeConsultationsCount:
            context.activeConsultationListResponse?.length,
          hasActiveConsultations:
            context.activeConsultationListResponse?.length > 0,
          decision:
            "Will check shouldShowDependentSelection guard (fixed to prevent re-showing dependent screen)",
        })
      ),
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "isOutsideOperatingHours",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "shouldShowDependentSelection",
          },
        },
        {
          target: "matchingActiveConsultation",
          guard: {
            type: "shouldEnterMatchingState",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
        },
      ],
    },
    promptingDirectToDoctorNextStepsWithPromoCodeOrSelectingDependents: {
      entry: createDecisionStateLogger<Context>(
        "promptingDirectToDoctorNextStepsWithPromoCodeOrSelectingDependents",
        (context) => ({
          hasDependents: context.memberDependantsList?.length > 0,
          dependentsCount: context.memberDependantsList?.length,
          selectedDependent: context.selectedDependent,
          isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          activeConsultationsCount:
            context.activeConsultationListResponse?.length,
          hasActiveConsultations:
            context.activeConsultationListResponse?.length > 0,
          decision:
            "Will check shouldShowDependentSelection guard, then shouldEnterMatchingState for idempotency",
        })
      ),
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "isOutsideOperatingHours",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "shouldShowDependentSelection",
          },
        },
        {
          target: "matchingActiveConsultation",
          guard: {
            type: "shouldEnterMatchingState",
          },
        },
        {
          target: "promptingDirectToDoctorNextStepsWithPromoCode",
          guard: {
            type: "hasValidDoctorPromoCodeAndIsEnhancedPaymentsDisabled",
          },
        },
        {
          target: "promptingDirectToDoctorNextSteps",
        },
      ],
    },
    promptingDirectToDoctorNextStepsOrSelectingDependents: {
      entry: createDecisionStateLogger<Context>(
        "promptingDirectToDoctorNextStepsOrSelectingDependents",
        (context) => ({
          hasDependents: context.memberDependantsList?.length > 0,
          dependentsCount: context.memberDependantsList?.length,
          selectedDependent: context.selectedDependent,
          isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          activeConsultationsCount:
            context.activeConsultationListResponse?.length,
          hasActiveConsultations:
            context.activeConsultationListResponse?.length > 0,
          decision:
            "Will check shouldShowDependentSelection guard (fixed to prevent re-showing dependent screen)",
        })
      ),
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "isOutsideOperatingHours",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "shouldShowDependentSelection",
          },
        },
        {
          target: "matchingActiveConsultation",
          guard: {
            type: "shouldEnterMatchingState",
          },
        },
        {
          target: "promptingDirectToDoctorNextSteps",
        },
      ],
    },
    showingPaymentOptions: {
      entry: "clearOneVoucherRedemptionResponseAndPin",
      on: {
        [eventNames.CARD_PAYMENT_BUTTON_CLICKED]: {
          target: "startingConsultation",
        },
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "promptingNurseConsultNextSteps",
            guard: { type: "isNurseConsultationSelected" },
            actions: [{ type: "clearCapturedDiscountVoucher" }],
          },
          {
            target: "promptingDirectToDoctorNextSteps",
            actions: [{ type: "clearCapturedDiscountVoucher" }],
          },
        ],
        [eventNames.VOUCHER_BUTTON_CLICKED]: {
          target: "selectingVoucher",
        },
        [eventNames.ONE_VOUCHER_BUTTON_CLICKED]: {
          target: "showingOneVoucherRedemption",
        },
        [eventNames.DISCOUNT_VOUCHER_SELECTED]: {
          actions: { type: "addSelectedDiscountVoucherToContext" },
        },
        [eventNames.APPLY_DISCOUNT_VOUCHER]: {
          target: "applyingDiscountVoucher",
          actions: { type: "addCapturedDiscountVoucherCodeToContext" },
          guard: "isDiscountVouchersFeatureEnabled",
        },
        [eventNames.ADD_A_DISCOUNT_VOUCHER_CLICKED]: {
          actions: {
            type: "removeApplyDiscountVoucherResponseErrorFromContext",
          },
        },
      },
    },
    applyingDiscountVoucher: {
      entry: "clearOnApplyDiscountVoucher",
      invoke: {
        id: "applyDiscountVoucher",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "showingPaymentOptions",
          actions: { type: "addApplyDiscountVoucherResponseToContext" },
        },
        onError: {
          target: "showingPaymentOptions",
          actions: { type: "addApplyDiscountVoucherResponseErrorToContext" },
        },
        src: "applyDiscountVoucher",
      },
    },
    selectingVoucher: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingPaymentOptions",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "startingConsultation",
          actions: [
            { type: "addSelectedVoucherOrPromoToContext" },
            { type: "addIsPromoOrVoucherCodeAppliedToContext" },
          ],
        },
      },
    },
    showingOneVoucherRedemption: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "showingPaymentOptions",
        },
        [eventNames.ONE_VOUCHER_CONTINUE_BUTTON_CLICKED]: {
          target: "startingConsultation",
          actions: [{ type: "addOneVoucherPinToContext" }],
        },
      },
    },
    promptingDirectToDoctorNextStepsWithPromoCode: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "promptingDirectToDoctorNextSteps",
          actions: [{ type: "addIsPromoOrVoucherCodeAppliedToContext" }],
        },
        [eventNames.SKIP_BUTTON_CLICKED]: {
          target: "promptingDirectToDoctorNextSteps",
          actions: [{ type: "addIsDirectToDoctorToContext" }],
        },
      },
    },
    promptingDirectToDoctorNextSteps: {
      entry: [
        "clearSelectedDiscountVoucher",
        createStateEntryLogger<Context>(
          "promptingDirectToDoctorNextSteps (What Happens Next)",
          (context) => ({
            selectedDependent: context.selectedDependent,
            dependentsCount: context.memberDependantsList?.length,
            isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          })
        ),
      ],
      on: {
        [eventNames.FIND_A_DOCTOR_BUTTON_CLICKED]: {
          target: "findADoctor",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: [
          {
            target: "showingPaymentOptions",
            guard: { type: "isEnhancedPaymentOptionsEnabled" },
            actions: { type: "addIsDirectToDoctorToContext" },
          },
          {
            target: "startingConsultation",
            actions: { type: "addIsDirectToDoctorToContext" },
          },
        ],
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "fetchingPrimarySymptoms",
            guard: { type: "hasPrimarySymptoms" },
          },
          {
            target: "selectConsultationTypeOrDefault",
            actions: { type: "removeIsDirectToDoctorToContext" },
          },
        ],
      },
    },
    selectingActiveConsultationOrStartingNewConsultation: {
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "hasNoActiveConsultationsAndIsOutsideOperatingHours",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "shouldSkipActiveConsultationsScreen",
          },
        },
        {
          target: "selectingActiveConsultation",
          guard: {
            type: "hasAtLeastOneActiveConsultations",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "hasDependentsAndNoActiveConsultations",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
          guard: {
            type: "hasNoDependentsAndNoActiveConsultations",
          },
        },
      ],
    },
    selectingActiveConsultation: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.ACTIVE_CONSULTATION_SELECTED]: {
          target: "activeConsultationOutcome",
          actions: [
            { type: "addSelectedConsultationToContext" },
            { type: "addConsultationOutcomeResponseToContext" },
          ],
        },
        [eventNames.START_CONSULTATION_BUTTON_CLICKED]: {
          target: "selectingConsultationTypeOrOutsideOperatingHours",
          actions: [{ type: "resetSelectedDependent" }],
        },
      },
    },
    selectingConsultationTypeOrOutsideOperatingHours: {
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "hasActiveConsultationsAndIsOutsideOperatingHours",
          },
        },
        {
          target: "selectConsultationType",
        },
      ],
    },
    activeConsultationOutcome: {
      always: [
        {
          target: "outsideOperatingHours",
          guard: {
            type: "hasActiveConsultationsAndIsOutsideOperatingHours",
          },
        },
        {
          target: "outstandingPayment",
          guard: {
            type: "selectedConsultationHasOutstandingPayment",
          },
        },
        {
          target: "outstandingRefund",
          guard: {
            type: "selectedConsultationHasOutstandingRefund",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
          guard: {
            type: "selectedConsultationHasOutstandingPaymentWithNoCharge",
          },
        },
        {
          target: "findAHospital",
          guard: {
            type: "userHasFindAHospitalOutcome",
          },
        },
        {
          target: "paymentFailure",
          guard: {
            type: "userHasUnsuccessfulPaymentOutcome",
          },
        },
        {
          target: "promptingFindADoctor",
          guard: {
            type: "userHasFindADoctorOutcome",
          },
        },
        {
          target: "serviceRating",
          guard: {
            type: "activeUserHasCompletedOutcome",
          },
        },
        {
          target: "exit",
          guard: {
            type: "selectedConsultationHasNoRefund",
          },
        },
      ],
      on: {
        [eventNames.USER_REDIRECTED]: {
          target: "waitingForConsultationOutcome",
        },
      },
    },
    outstandingPayment: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "selectConsultationType",
            guard: {
              type: "shouldSkipActiveConsultationsScreen",
            },
          },
          {
            target: "selectingActiveConsultation",
          },
        ],
        [eventNames.USER_REQUESTED_PAYMENT]: {
          target: "waitingForRequestPaymentOutcome",
          actions: "addPaymentRedirectUrlToContext",
        },
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    promptingNurseConsultNextSteps: {
      entry: [
        "clearSelectedDiscountVoucher",
        createStateEntryLogger<Context>(
          "promptingNurseConsultNextSteps (What Happens Next)",
          (context) => ({
            selectedDependent: context.selectedDependent,
            dependentsCount: context.memberDependantsList?.length,
            isNurseConsultationSelected: context.isNurseConsultationSelected,
          })
        ),
      ],
      always: {
        target: "outsideOperatingHours",
        guard: {
          type: "hasActiveConsultationsAndIsOutsideOperatingHours",
        },
      },
      invoke: {
        id: "promptingNurseConsultNextSteps",
        input: ({ context }) => ({ context }),
        onDone: {
          actions: {
            type: "addConsultationRatesResponseToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getMemberConsultationRates",
      },
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "redirectFromPromptingNextSteps",
        },
        [eventNames.CONTINUE_BUTTON_CLICKED]: [
          {
            target: "showingPaymentOptions",
            guard: { type: "isEnhancedPaymentOptionsEnabled" },
          },
          {
            target: "startingConsultation",
          },
        ],
        [eventNames.MORE_INFO_BUTTON_CLICKED]: {
          target: "promptingNurseConsultMoreInfo",
        },
        [eventNames.FIND_A_DOCTOR_BUTTON_CLICKED]: {
          target: "findADoctor",
        },
      },
    },
    promptingNurseConsultMoreInfo: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "promptingNurseConsultNextSteps",
        },
      },
    },
    redirectFromPromptingNextSteps: {
      always: [
        {
          target: "selectConsultationType",
          guard: {
            type: "hasNoDependentsAndNoActiveConsultations",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "shouldSkipActiveConsultationsScreen",
          },
        },
        {
          target: "selectingActiveConsultation",
          guard: {
            type: "hasActiveConsultations",
          },
        },
        {
          target: "selectingDependents",
          guard: {
            type: "hasDependentsAndNoActiveConsultations",
          },
        },
        {
          target: "selectConsultationType",
        },
      ],
    },
    selectingDependents: {
      entry: createStateEntryLogger<Context>(
        "selectingDependents",
        (context) => ({
          isDirectToDoctorSelected: context.isDirectToDoctorSelected,
          isNurseConsultationSelected: context.isNurseConsultationSelected,
          isDoctorMedicineBundleSelected:
            context.isDoctorMedicineBundleSelected,
          selectedDependent: context.selectedDependent,
          dependentsCount: context.memberDependantsList?.length,
        })
      ),
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.DEPENDANT_SELECTED]: {
          target: "updatingDependentsOrPromptingNextSteps",
          actions: "addSelectedDependentToContext",
        },
      },
    },
    updatingDependentsOrPromptingNextSteps: {
      always: [
        {
          target: "updatingMemberDependents",
          guard: {
            type: "isSelectedDependentNotMainMember",
          },
        },
        {
          target: "promptingDirectToDoctorOrNurseConsultNextSteps",
        },
      ],
    },
    updatingMemberDependents: {
      invoke: {
        id: "updatingMemberDependents",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "promptingDirectToDoctorOrNurseConsultNextSteps",
        },
        onError: {
          target: "genericError",
        },
        src: "updateMemberDependents",
      },
    },
    promptingDirectToDoctorOrNurseConsultNextSteps: {
      always: [
        {
          target: "matchingActiveConsultation",
          guard: {
            type: "shouldEnterMatchingState",
          },
        },
        {
          target: "promptingDirectToDoctorNextSteps",
          guard: {
            type: "isDirectToDoctorSelected",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
        },
      ],
    },
    matchingActiveConsultation: {
      invoke: {
        id: "matchingActiveConsultation",
        input: ({ context }) => ({ context }),
        src: "findExistingConsultation",
        onDone: {
          actions: assign({
            matchedConsultation: ({ event }) => event.output,
          }),
          target: "routingBasedOnMatch",
        },
        onError: {
          actions: assign({
            detectionError: ({ event }) =>
              event.error instanceof Error
                ? event.error
                : new Error(String(event.error)),
          }),
          target: "genericError",
        },
      },
    },
    routingBasedOnMatch: {
      always: [
        {
          target: "outstandingPayment",
          guard: {
            type: "hasMatchedConsultationWithPaymentRequested",
          },
          actions: assign({
            selectedConsultation: ({ context }) =>
              context.matchedConsultation
                ? mapMatchedToSelectedConsultation(context.matchedConsultation)
                : null,
          }),
        },
        {
          target: "breakoutDisclaimer",
          guard: {
            type: "hasMatchedConsultationWithPaymentSucceeded",
          },
          actions: assign({
            skipPayment: true,
            selectedConsultation: ({ context }) =>
              context.matchedConsultation
                ? mapMatchedToSelectedConsultation(context.matchedConsultation)
                : null,
          }),
        },
        {
          target: "waitingForConsultationOutcome",
          guard: {
            type: "hasMatchedConsultationWithUnknownOutcome",
          },
          actions: assign({
            selectedConsultation: ({ context }) =>
              context.matchedConsultation
                ? mapMatchedToSelectedConsultation(context.matchedConsultation)
                : null,
          }),
        },
        {
          target: "promptingDirectToDoctorNextSteps",
          guard: {
            type: "isDirectToDoctorSelected",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "hasNoConsultationTypeSelected",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
        },
      ],
    },
    getActiveConsultationsError: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    startingConsultation: {
      invoke: {
        id: "startingConsultation",
        input: ({ context }) => ({ context }),
        src: "startConsultation",
        onDone: {
          target: "breakoutDisclaimer",
          actions: {
            type: "addStartConsultationResponseToContext",
          },
        },
        onError: [
          {
            target: "showingOneVoucherRedemption",
            guard: {
              type: "isEnhancedPaymentOptionsEnabledAndHasOneVoucherPinInContext",
            },
            actions: {
              type: "addInitiatePaymentResponseErrorToContext",
            },
          },
          {
            target: "genericError",
          },
        ],
      },
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    breakoutDisclaimer: {
      on: {
        [eventNames.USER_REDIRECTED]: {
          target: "waitingForConsultationOutcome",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "redirectFromBreakoutDisclaimer",
        },
      },
    },
    redirectFromBreakoutDisclaimer: {
      entry: "clearSelectedDiscountVoucher",
      always: [
        {
          target: "showingPaymentOptions",
          guard: {
            type: "isEnhancedPaymentOptionsEnabledAndPaymentNotSkipped",
          },
        },
        {
          target: "selectConsultationType",
          guard: {
            type: "shouldSkipActiveConsultationsScreen",
          },
        },
        {
          target: "promptingDirectToDoctorNextSteps",
          guard: {
            type: "isDirectToDoctorSelected",
          },
        },
        {
          target: "promptingNurseConsultNextSteps",
          guard: {
            type: "isNurseConsultationSelected",
          },
        },
        {
          target: "selectingActiveConsultation",
          guard: {
            type: "hasActiveConsultations",
          },
        },
      ],
    },
    waitingForRequestPaymentOutcome: {
      invoke: {
        id: "waitingForRequestPaymentOutcome",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "waitingForConsultationOutcome",
          actions: "redirectToPaymentPage",
        },
        onError: {
          target: "genericError",
        },
        src: "getMemberRequestPaymentOutcome",
      },
    },
    waitingForConsultationOutcome: {
      after: {
        CONSULTATION_INTERVAL: {
          target: "fetchingConsultationOutcome",
        },
      },
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    fetchingConsultationOutcome: {
      invoke: {
        id: "fetchingConsultationOutcome",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "processingConsultationOutcome",
          actions: {
            type: "addConsultationOutcomeResponseToContext",
          },
        },
        onError: {
          target: "genericError",
        },
        src: "getConsultationOutcome",
      },
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    processingConsultationOutcome: {
      always: [
        {
          target: "promptingNurseConsultNextSteps",
          guard: {
            type: "userHasArchivedConsultation",
          },
        },
        {
          target: "findAHospital",
          guard: {
            type: "userHasFindAHospitalOutcome",
          },
        },
        {
          target: "paymentFailure",
          guard: {
            type: "userHasUnsuccessfulPaymentOutcome",
          },
        },
        {
          target: "promptingFindADoctor",
          guard: {
            type: "userHasFindADoctorOutcome",
          },
        },
        {
          target: "serviceRating",
          guard: {
            type: "userHasCompletedOutcome",
          },
        },
        {
          target: "outstandingRefund",
          guard: {
            type: "userHasOutstandingRefund",
          },
        },
        {
          target: "exit",
          guard: {
            type: "timeoutExceeded",
          },
        },
        {
          target: "breakoutDisclaimer",
          guard: {
            type: "hasConsultationIdInQueryParam",
          },
        },
        {
          target: "waitingForConsultationOutcome",
        },
      ],
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    genericError: {
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    outsideOperatingHours: {
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    submittingUserRating: {
      invoke: {
        id: "submittingUserRating",
        input: ({ context }) => ({ context }),
        onDone: {
          target: "submitRatingComplete",
        },
        onError: {
          target: "submitRatingError",
        },
        src: "submitRating",
      },
    },
    submitRatingComplete: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    submitRatingError: {
      on: {
        [eventNames.RATING_TRY_AGAIN]: {
          target: "serviceRating",
        },
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    outstandingRefund: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "exit",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    serviceRating: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "submittingUserRating",
          actions: "addUserRatingToContext",
        },
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    findAHospital: {
      type: "final",
    },
    promptingFindADoctor: {
      on: {
        [eventNames.CONTINUE_BUTTON_CLICKED]: {
          target: "findADoctor",
        },
        [eventNames.BACK_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    findADoctor: {
      on: {
        [eventNames.BACK_BUTTON_CLICKED]: [
          {
            target: "promptingDirectToDoctorNextSteps",
            guard: {
              type: "isDirectToDoctorSelected",
            },
          },
          { target: "promptingFindADoctor" },
        ],
      },
    },
    paymentFailure: {
      on: {
        [eventNames.GO_HOME_BUTTON_CLICKED]: {
          target: "exit",
        },
      },
    },
    exit: {
      type: "final",
    },
  },
});
